/**
 * エラーハンドリング共通ユーティリティ
 *
 * @module utils/errorHandler
 * @created 2025-11-27
 * @description
 *   システム全体で統一されたエラーハンドリングを提供
 *   - 多様なエラー形式を正規化
 *   - HTTPステータスコードに応じた日本語メッセージ
 *   - フィールド別バリデーションエラーの整形
 */

import axios from 'axios';
import type { NormalizedError, FieldError } from '../types/error';
import { FIELD_LABELS } from '../types/error';

/**
 * HTTPステータスコードに応じたデフォルトメッセージ
 */
const STATUS_MESSAGES: Record<number, string> = {
  400: 'リクエストが不正です。入力内容を確認してください。',
  401: '認証が必要です。再度ログインしてください。',
  403: 'この操作を行う権限がありません。',
  404: '対象のデータが見つかりません。',
  409: '同一のデータが既に登録されています。',
  422: '入力内容に問題があります。確認してください。',
  500: 'サーバーエラーが発生しました。しばらく待ってから再度お試しください。',
  502: 'サーバーとの通信に問題が発生しました。',
  503: 'サービスが一時的に利用できません。しばらく待ってから再度お試しください。',
};

/**
 * バックエンドのレスポンスボディから、フィールド別エラー配列を抽出・正規化する。
 *
 * 複数のBE形状に対応する（フォームUX_バリデーション共通設計方針 §5.2）:
 *   - `errors` / `error` いずれのキーでも可
 *   - 要素が `{ field, message }`（統一形状）または `{ path, msg }`（express-validator生）
 *   - `error` が JSON文字列化された配列の場合も解釈
 */
function extractFieldErrors(responseData: unknown): FieldError[] | null {
  if (!responseData || typeof responseData !== 'object') return null;
  const data = responseData as Record<string, unknown>;

  let rawArray: unknown = null;
  if (Array.isArray(data.errors)) {
    rawArray = data.errors;
  } else if (Array.isArray(data.error)) {
    rawArray = data.error;
  } else if (typeof data.error === 'string') {
    // JSON文字列化された配列（旧 middleware/validation.ts 形式）を吸収
    const trimmed = data.error.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) rawArray = parsed;
      } catch {
        // JSONでなければフィールドエラーとして扱わない
      }
    }
  }

  if (!Array.isArray(rawArray)) return null;

  const fieldErrors: FieldError[] = rawArray
    .map((item): FieldError | null => {
      if (!item || typeof item !== 'object') return null;
      const rec = item as Record<string, unknown>;
      const field =
        typeof rec.field === 'string'
          ? rec.field
          : typeof rec.path === 'string'
          ? rec.path
          : undefined;
      const message =
        typeof rec.message === 'string'
          ? rec.message
          : typeof rec.msg === 'string'
          ? rec.msg
          : undefined;
      if (!field || !message) return null;
      return {
        field,
        message,
        label: FIELD_LABELS[field] || field,
      };
    })
    .filter((e): e is FieldError => e !== null);

  return fieldErrors.length > 0 ? fieldErrors : null;
}

/**
 * 任意のエラーを正規化された形式に変換
 *
 * @param error - 任意のエラー（axios、Error、その他）
 * @param defaultMessage - デフォルトのエラーメッセージ
 * @returns 正規化されたエラー構造
 *
 * @example
 * ```typescript
 * try {
 *   await api.post('/endpoint', data);
 * } catch (error) {
 *   const normalized = normalizeError(error, '保存に失敗しました');
 *   if (normalized.isAuthError) {
 *     // 認証エラー処理
 *   }
 * }
 * ```
 */
export function normalizeError(
  error: unknown,
  defaultMessage = 'エラーが発生しました'
): NormalizedError {
  // ネットワークエラー（オフライン等）
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return {
      message: 'ネットワークエラーが発生しました。接続を確認してください。',
      isNetworkError: true,
    };
  }

  // Axiosエラーの場合
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const responseData = error.response?.data;

    // 認証エラー判定
    const isAuthError = status === 401 || status === 403;

    // ネットワークエラー判定（レスポンスなし）
    if (!error.response) {
      return {
        message: 'ネットワークエラーが発生しました。接続を確認してください。',
        isNetworkError: true,
      };
    }

    // バリデーションエラー（フィールド配列）
    // 対応する形状（フォームUX_バリデーション共通設計方針 §5, §6）:
    //   { errors: [{ field, message }] }        … 統一形状
    //   { error:  [{ field, message }] }         … 旧・単数キー形状
    //   { errors: [{ path, msg }] }              … express-validator 生配列
    //   { error:  "[{...}]" }                    … JSON文字列化された配列
    const fieldErrors = extractFieldErrors(responseData);
    if (fieldErrors && fieldErrors.length > 0) {
      return {
        message:
          typeof responseData?.message === 'string' && responseData.message
            ? responseData.message
            : '入力内容を確認してください',
        statusCode: status,
        fieldErrors,
        isAuthError,
      };
    }

    // バックエンドからのメッセージがある場合
    const backendMessage = responseData?.message || responseData?.error;
    if (typeof backendMessage === 'string') {
      return {
        message: backendMessage,
        statusCode: status,
        isAuthError,
      };
    }

    // ステータスコードに応じたデフォルトメッセージ
    const statusMessage = status ? STATUS_MESSAGES[status] : undefined;
    if (statusMessage) {
      return {
        message: statusMessage,
        statusCode: status,
        isAuthError,
      };
    }

    // それ以外のAxiosエラー
    return {
      message: status
        ? `エラーが発生しました（ステータス: ${status}）`
        : defaultMessage,
      statusCode: status,
      isAuthError,
    };
  }

  // 通常のErrorの場合
  if (error instanceof Error) {
    return {
      message: error.message || defaultMessage,
    };
  }

  // その他
  return {
    message: defaultMessage,
  };
}

/**
 * 正規化されたエラーを表示用文字列に変換
 *
 * @param error - 正規化されたエラー
 * @returns 表示用文字列（フィールドエラーは改行区切り）
 *
 * @example
 * ```typescript
 * const normalized = normalizeError(error);
 * const displayMessage = formatErrorForDisplay(normalized);
 * toast.error(displayMessage);
 * ```
 */
export function formatErrorForDisplay(error: NormalizedError): string {
  // フィールドエラーがある場合は詳細を表示
  if (error.fieldErrors && error.fieldErrors.length > 0) {
    const errorMessages = error.fieldErrors.map(
      (err) => `【${err.label || err.field}】${err.message}`
    );
    return errorMessages.join('\n');
  }

  return error.message;
}

/**
 * シンプルなエラーメッセージ抽出（既存コードとの互換性のため）
 *
 * @param error - 任意のエラー
 * @param defaultMessage - デフォルトのエラーメッセージ
 * @returns ユーザー向けエラーメッセージ文字列
 *
 * @description
 *   既存の extractErrorMessage / formatErrorMessage の置き換え用
 *   normalizeError + formatErrorForDisplay のショートカット
 *
 * @example
 * ```typescript
 * try {
 *   await api.delete(`/items/${id}`);
 * } catch (error) {
 *   toast.error(extractErrorMessage(error, '削除に失敗しました'));
 * }
 * ```
 */
export function extractErrorMessage(
  error: unknown,
  defaultMessage = 'エラーが発生しました'
): string {
  const normalized = normalizeError(error, defaultMessage);
  return formatErrorForDisplay(normalized);
}

/**
 * フィールド名 → エラーメッセージのマップを返す。
 *
 * フォームは戻り値を各入力の `error` / `helperText` に割り当てる
 * （フォームUX_バリデーション共通設計方針 §6）。
 *
 * @param error - 任意のエラー
 * @returns { フィールド名: メッセージ }。フィールド級エラーが無ければ空オブジェクト
 *
 * @example
 * ```typescript
 * catch (error) {
 *   const fieldMap = getFieldErrorMap(error);
 *   setFormErrors(fieldMap);
 *   toast.error(normalizeError(error).message); // 要約のみ
 * }
 * ```
 */
export function getFieldErrorMap(error: unknown): Record<string, string> {
  const normalized = normalizeError(error);
  const map: Record<string, string> = {};
  normalized.fieldErrors?.forEach((fe) => {
    if (!map[fe.field]) {
      map[fe.field] = fe.message;
    }
  });
  return map;
}

/**
 * 認証エラーかどうかを判定
 *
 * @param error - 任意のエラー
 * @returns 認証エラーの場合true
 */
export function isAuthenticationError(error: unknown): boolean {
  const normalized = normalizeError(error);
  return normalized.isAuthError === true;
}

/**
 * ネットワークエラーかどうかを判定
 *
 * @param error - 任意のエラー
 * @returns ネットワークエラーの場合true
 */
export function isNetworkError(error: unknown): boolean {
  const normalized = normalizeError(error);
  return normalized.isNetworkError === true;
}
